import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import './LiveChatWidget.css';
import FAQ_DATA from '../../data/chatWidgetFAQData.json';

const SOCKET_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001');
const STATUS_CONFIG = {
  open: { label: 'Open', color: '#3b82f6', icon: 'fa-circle-dot' },
  in_progress: { label: 'In Progress', color: '#f59e0b', icon: 'fa-circle-half-stroke' },
  resolved: { label: 'Resolved', color: '#10b981', icon: 'fa-circle-check' },
  closed: { label: 'Closed', color: '#6b7280', icon: 'fa-circle-xmark' },
};

export default function LiveChatWidget({ onClose }) {
  const { keycloak } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [view, setView] = useState('main');
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [subject, setSubject] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Load persisted chat state from localStorage ───────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('lcw_chat_state');
    const savedMessages = localStorage.getItem('lcw_messages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.activeTab !== undefined) setActiveTab(parsed.activeTab);
        if (parsed.view !== undefined) setView(parsed.view);
        if (parsed.subject) setSubject(parsed.subject);
        if (parsed.firstMessage) setFirstMessage(parsed.firstMessage);
        if (parsed.newMessage) setNewMessage(parsed.newMessage);
      } catch (e) {
        console.error('[LiveChat] Failed to load chat state:', e);
      }
    }
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error('[LiveChat] Failed to load messages:', e);
      }
    }
  }, []);

  // ── Persist chat state to localStorage on changes ────────────────────────────
  useEffect(() => {
    localStorage.setItem(
      'lcw_chat_state',
      JSON.stringify({
        activeTab,
        view,
        subject,
        firstMessage,
        newMessage,
      })
    );
  }, [activeTab, view, subject, firstMessage, newMessage]);

  // ── Persist messages when they change ───────────────────────────────────────
  useEffect(() => {
    if (activeTicket && messages.length > 0) {
      localStorage.setItem('lcw_messages', JSON.stringify(messages));
    } else {
      localStorage.removeItem('lcw_messages');
    }
  }, [messages, activeTicket]);

  // ── Socket.io ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = keycloak?.token;
    if (!token) return;
    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('new_message', (msg) => {
      if (activeTicket && msg.ticketId === activeTicket.id) setMessages((prev) => [...prev, msg]);
    });
    socket.on('typing', ({ isAdmin, isTyping }) => {
      if (isAdmin) setAdminTyping(isTyping);
    });
    return () => socket.disconnect();
  }, [keycloak?.token, activeTicket?.id]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeTicket) return;
    socket.emit('join_ticket', activeTicket.id);
    return () => socket.emit('leave_ticket', activeTicket.id);
  }, [activeTicket?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Fetch user's own tickets ──────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/support/tickets');
      if (res.data.success) setTickets(res.data.data);
    } catch (err) {
      console.error('[LiveChat] fetchTickets error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openTicket = async (ticket) => {
    setActiveTicket(ticket);
    setMessages([]);
    setView('chat');
    localStorage.setItem('lcw_ticketId', ticket.id);
    try {
      const res = await axios.get(`/support/tickets/${ticket.id}/messages`);
      if (res.data.success) setMessages(res.data.data);
    } catch (err) {
      console.error('[LiveChat] openTicket error:', err);
    }
  };

  const createTicket = async () => {
    if (!subject.trim() || !firstMessage.trim() || creating) return;
    setCreating(true);
    try {
      const res = await axios.post('/support/tickets', {
        subject: subject.trim(),
        message: firstMessage.trim(),
      });
      if (!res.data.success) return;
      const ticket = res.data.data;
      setTickets((prev) => [ticket, ...prev]);
      setActiveTicket(ticket);
      setView('chat');
      setSubject('');
      setFirstMessage('');
      // Fetch messages so the first message (user's issue) appears immediately
      try {
        const msgRes = await axios.get(`/support/tickets/${ticket.id}/messages`);
        if (msgRes.data.success) setMessages(msgRes.data.data);
      } catch (msgErr) {
        console.error('[LiveChat] createTicket fetch messages error:', msgErr);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async (imageUrl = null) => {
    if (!imageUrl && (!newMessage.trim() || !activeTicket || sending)) return;
    setSending(true);
    try {
      const res = await axios.post(`/support/tickets/${activeTicket.id}/messages`, {
        message_text: imageUrl ? '' : newMessage.trim(),
        image_url: imageUrl,
      });
      if (res.data.success) {
        setMessages((prev) => {
          const newMsg = res.data.data;
          const exists = prev.some((msg) => msg.id === newMsg.id);
          if (exists) return prev;
          return [...prev, newMsg];
        });
        if (!imageUrl) setNewMessage('');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // ── Persist messages when they change ───────────────────────────────────────
  useEffect(() => {
    if (activeTicket) {
      localStorage.setItem('lcw_messages', JSON.stringify(messages));
    }
  }, [messages, activeTicket]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeTicket) return;
    setSending(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post('/support/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) await sendMessage(res.data.data.url);
    } catch (err) {
      alert('Upload failed');
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    if (!socketRef.current || !activeTicket) return;
    socketRef.current.emit('typing', { ticketId: activeTicket.id, isTyping: true });
    clearTimeout(handleTyping._t);
    handleTyping._t = setTimeout(
      () => socketRef.current?.emit('typing', { ticketId: activeTicket.id, isTyping: false }),
      1500
    );
  };

  // ── Render Helpers ────────────────────────────────────────────────────────
  const renderHome = () => {
    const contactEmail = process.env.REACT_APP_CONTACT_US || 'zeoleezh@gmail.com';
    const latestTicket = tickets[0];
    return (
      <>
        <div
          className="lcw-card lcw-action-card"
          onClick={() => (latestTicket ? openTicket(latestTicket) : setView('new'))}
          style={{ padding: '16px 20px', display: 'block' }}
        >
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
            Recent message
          </div>
          {latestTicket ? (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div
                className="lcw-status-icon"
                style={{
                  background: '#f8fafc',
                  color: '#6366f1',
                  width: '36px',
                  height: '36px',
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-headset" />
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>
                    Support Team
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {new Date(latestTicket.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#64748b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {latestTicket.subject}
                </div>
              </div>
              {latestTicket.unread_user_count > 0 && (
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    background: '#ef4444',
                    borderRadius: '50%',
                  }}
                />
              )}
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '13px' }}>
              No recent messages. Start a conversation to get help.
            </div>
          )}
        </div>

        <div className="lcw-card lcw-action-card" onClick={() => setView('new')}>
          <span>Send us a message</span>
          <i className="fas fa-paper-plane" />
        </div>
        <a
          href={`mailto:${contactEmail}`}
          className="lcw-card lcw-action-card"
          style={{ textDecoration: 'none' }}
        >
          <span>Email Support</span>
          <i className="fas fa-envelope" />
        </a>
      </>
    );
  };

  const renderMessages = () => (
    <div className="lcw-messages-tab">
      <div
        className="lcw-card lcw-action-card"
        onClick={() => setView('new')}
        style={{ marginBottom: '20px' }}
      >
        <span>Start a new conversation</span>
        <i className="fas fa-paper-plane" />
      </div>

      <div style={{ padding: '0 8px', marginBottom: '8px' }}>
        <h5 style={{ margin: 0, color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>
          Your Conversations
        </h5>
      </div>
      {loading ? (
        <div className="lcw-loading">
          <i className="fas fa-circle-notch fa-spin lcw-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div
          className="lcw-card"
          style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}
        >
          <i className="fas fa-comments-alt" style={{ fontSize: '32px', marginBottom: '12px' }} />
          <p style={{ margin: 0, fontSize: '13px' }}>No messages yet. Start a chat!</p>
        </div>
      ) : (
        tickets.map((t) => {
          const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
          return (
            <div
              key={`ticket-${t.id}-${Math.random()}`}
              className="lcw-ticket-card"
              onClick={() => openTicket(t)}
            >
              <div
                className="lcw-status-icon"
                style={{ background: '#f8fafc', color: cfg.color, width: '36px', height: '36px' }}
              >
                <i className={`fas ${cfg.icon}`} />
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#1e293b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {t.subject}
                  </span>
                  <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                    {new Date(t.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>#{t.id}</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: cfg.color }}>
                    {cfg.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderHelp = () => (
    <div className="lcw-help-tab">
      {['Finding Prospects', 'Credits & Billing', 'Platform Usage'].map((cat) => (
        <div key={cat} className="lcw-card" style={{ marginBottom: '12px', padding: '16px' }}>
          <h6 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 800, color: '#1e293b' }}>
            {cat}
          </h6>
          {FAQ_DATA.filter((f) => f.category === cat).map((faq, i) => (
            <div
              key={i}
              style={{ borderTop: i > 0 ? '1px solid #f1f5f9' : 'none', padding: '10px 0' }}
            >
              <div
                style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}
              >
                {faq.q}
              </div>
              <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>{faq.a}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="lcw-widget">
      <div className="lcw-header-dark">
        <div className="lcw-header-top">
          <div />
          <button className="lcw-close-btn" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>
        <h1>
          Need support?
          <br />
          How can we help?
        </h1>
      </div>

      <div className="lcw-body-scroll">
        {activeTab === 'home' && renderHome()}
        {activeTab === 'messages' && renderMessages()}
        {activeTab === 'help' && renderHelp()}
      </div>

      <div className="lcw-nav">
        <div
          className={`lcw-nav-item ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <i className="fas fa-home" />
          <span>Home</span>
        </div>
        <div
          className={`lcw-nav-item ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <i className="fas fa-comments" />
          <span>Messages</span>
        </div>
        <div
          className={`lcw-nav-item ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          <i className="fas fa-circle-question" />
          <span>Help</span>
        </div>
      </div>

      {view === 'chat' && activeTicket && (
        <div className="lcw-chat-view">
          <div className="lcw-chat-header">
            <button
              className="lcw-close-btn"
              onClick={() => setView('main')}
              style={{ background: 'transparent' }}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <div>
              <h4>{activeTicket.subject}</h4>
              <span style={{ fontSize: '11px', opacity: 0.6 }}>#{activeTicket.id}</span>
            </div>
          </div>
          <div className="lcw-messages-container">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`lcw-msg-bubble ${msg.is_admin_reply ? 'lcw-admin-bubble' : 'lcw-user-bubble'}`}
              >
                {msg.image_url && (
                  <img
                    src={msg.image_url}
                    alt="att"
                    style={{ width: '100%', borderRadius: '8px', marginBottom: '8px' }}
                  />
                )}
                <p style={{ margin: 0 }}>{msg.message_text}</p>
                <div
                  style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7, textAlign: 'right' }}
                >
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {activeTicket &&
          (activeTicket.status === 'resolved' || activeTicket.status === 'closed') ? (
            <div className="lcw-input-area lcw-input-disabled">
              <i className="fas fa-circle-xmark" style={{ fontSize: '18px', color: '#94a3b8' }} />
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                This ticket has been {activeTicket.status}. You can no longer send messages.
              </span>
            </div>
          ) : (
            <div className="lcw-input-area">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageSelect}
              />
              <button
                className="lcw-send-btn"
                style={{ background: '#f4f7f9', color: '#64748b' }}
                onClick={() => fileInputRef.current.click()}
              >
                <i className="fas fa-image" />
              </button>
              <textarea
                className="lcw-intercom-input"
                rows={1}
                placeholder="Type a message..."
                value={newMessage}
                onChange={handleTyping}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />
              <button
                className="lcw-send-btn"
                onClick={() => sendMessage()}
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <i className="fas fa-circle-notch lcw-spin" />
                ) : (
                  <i className="fas fa-paper-plane" />
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {view === 'new' && (
        <div className="lcw-chat-view">
          <div className="lcw-chat-header">
            <button
              className="lcw-close-btn"
              onClick={() => setView('main')}
              style={{ background: 'transparent' }}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <h4>Start a conversation</h4>
          </div>
          <div className="lcw-body-scroll" style={{ marginTop: '0', padding: '24px' }}>
            <div className="lcw-form-group">
              <label className="lcw-form-label">What do you need help with?</label>
              <select
                className="lcw-form-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              >
                <option value="">Select a topic...</option>
                <option value="Technical Issue">Technical Issue</option>
                <option value="Billing & Payments">Billing & Payments</option>
                <option value="Account Support">Account Support</option>
                <option value="General Inquiry">General Inquiry</option>
                <option value="Feedback & Suggestions">Feedback & Suggestions</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="lcw-form-group">
              <label className="lcw-form-label">How can we help?</label>
              <textarea
                className="lcw-form-input lcw-form-textarea"
                rows={4}
                value={firstMessage}
                onChange={(e) => setFirstMessage(e.target.value)}
                placeholder="Type your message here..."
              />
            </div>
            <button
              className="lcw-brand-btn"
              onClick={createTicket}
              disabled={creating || !subject.trim() || !firstMessage.trim()}
            >
              {creating ? <i className="fas fa-circle-notch fa-spin lcw-spin" /> : 'Send Message'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
